import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  RefObject,
} from "react";
import { cls } from "cls";
import ValidationError from "../Pricing/AddToCart/ValidationError";
import { ConfigurationOption } from "../ProductDetailPageResult";
import ProductDetailVirtualGroupingMobileDrawer from "./ProductDetailVirtualGroupingMobileDrawer";
import GroupConfiguarationTiles from "./GroupConfigurationTile";
import Button from "~/common/Buttons";
import { VirtualGroupingOptionsToMap } from "./VirtualGroupingModel";
import useResizeObserver from "./Hooks/useResizeObserver";
import {
  moveVirtualGroupingTile,
  showEightVirtualGroupTileVariations,
} from "./VirtualGroupingHelper";
import isMobile from "~/common/helpers/isMobile";
import { VirtualGroupingContext } from "../useVirtualGroupingContext";

interface Props {
  virtualGroupMemberToDisplay: string;
  addToCartError?: ValidationError;
  selectedOptions: readonly ConfigurationOption[];
  handleClickVirtualGroupingVariation: (
    name: string,
    value: string,
    validOption: boolean
  ) => void;
}

const ProductDetailVirtualGroupingTile = ({
  virtualGroupMemberToDisplay,
  addToCartError,
  selectedOptions,
  handleClickVirtualGroupingVariation,
}: Props) => {
  const {
    virtualGroupingVariationsRef,
    virtualGroupingTileContainerRef,
    scrollPosition,
    setScrollPosition,
    isInitialPageLoadRef,
    virtualGroupingTileSelectedRef,
    virtualGroupingUpdatingFocus,
    virtualGroupOptionsMap,
    virtualGroupingMobileDialogRef,
  } = useContext(VirtualGroupingContext);

  const [
    selectedMasterTile,
    setSelectedMasterTile,
  ] = useState<VirtualGroupingOptionsToMap>({
    name: "",
    variations: [],
    validOptions: [],
  });
  const [hoveredProduct, setHoveredProduct] = useState<{
    [key: string]: string;
  }>({ name: "" });

  const { width } = useResizeObserver();

  /* Setting the scroll position after the screen repaints to combat the scroll position.
     being reset because when a virtual grouping tile is clicked state in the product detail index file updates causing the childs to all rerender. */
  useEffect(() => {
    if (scrollPosition > 0 && !!virtualGroupingTileContainerRef.current) {
      virtualGroupingTileContainerRef.current.scrollTo(0, scrollPosition);
    }
  }, [virtualGroupingTileContainerRef, scrollPosition]);

  /* Move selected virtual grouping tile to the first of the list if the selected virtual grouping tile is not top eight.
     For desktop only should happen once on initial load. For mobile it runs on every virtual grouping tile clicked that is not top eight. */
  useEffect(() => {
    if (!isInitialPageLoadRef.current && !isMobile("600px")) {
      moveVirtualGroupingTile(
        virtualGroupingVariationsRef,
        virtualGroupMemberToDisplay
      );
      isInitialPageLoadRef.current = true;
    }
    if (isMobile("600px")) {
      moveVirtualGroupingTile(
        virtualGroupingVariationsRef,
        virtualGroupMemberToDisplay
      );
    }
  }, [
    isInitialPageLoadRef,
    virtualGroupMemberToDisplay,
    virtualGroupingVariationsRef,
  ]);

  /* Setting focus back to the tile that was selected because the focus gets reset due to the unmounting and remounting when a virtual grouping is clicked.
     virtualGroupingTileSelectedRef will check if the ref is clicked and if it is then it then it will set the focus to the button clicked. */
  useEffect(() => {
    if (virtualGroupingTileSelectedRef.current) {
      const elem = document.querySelector(
        'div[data-testid="variationContainer"] button[aria-pressed="true"]'
      );
      if (elem instanceof HTMLElement) {
        elem.focus();
      }
    }
  }, [virtualGroupingTileSelectedRef, selectedOptions]);

  /* This is used to toggle the mobile drawer modal for the virtual grouping tiles. */
  const toggleShowMobileDrawerDialog = (
    dialogRef: RefObject<HTMLDialogElement>
  ) => {
    const dialog = dialogRef.current;
    const modalOpen = dialog?.hasAttribute("open");
    if (modalOpen) {
      dialog?.classList.add("translate-y-[100vh]", "backdrop:opacity-0");
      // This timeout is used to prevent the dialog from being hidden while the animation runs
      setTimeout(() => {
        dialog?.close();
        // Remove the transition classes so that the dialog and backdrop can be shown again later
        dialog?.classList.remove("translate-y-[100vh]", "backdrop:opacity-0");
      }, 150);
    } else {
      dialog?.showModal();
    }
  };

  /* Text options for the tile heading read out.
     If selected but not hovered then it shows the selecte items name.
     If hovered it shows the hovered items name. */

  const getVariationTextOption = (name: string) => {
    const findOptionIndex = selectedOptions.findIndex(
      (option) => option.name === name
    );
    if (selectedOptions[findOptionIndex]?.value && !hoveredProduct[name]) {
      return selectedOptions[findOptionIndex]?.value;
    }
    if (hoveredProduct[name]) {
      return hoveredProduct[name];
    }
    return `Select a ${name}`;
  };

  const onButtonHoverEnter = (
    name: string,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    setHoveredProduct({ [name]: e.currentTarget.value });
  };

  const onButtonHoverLeave = () => {
    setHoveredProduct({});
  };

  const onlyFocusOnKeyboardEvent = (e: React.KeyboardEvent) => {
    if (e.key === "Tab") {
      e.preventDefault();
      e.currentTarget.classList.add("virtualGroupingKeyboardFocus");
    }
  };

  const onClickPosition = () => {
    if (virtualGroupingTileContainerRef.current !== null) {
      setScrollPosition(virtualGroupingTileContainerRef.current.scrollTop);
    }
    virtualGroupingUpdatingFocus(true);
  };

  // Virtual Grouping Optiosn map but with the variations set to the virtualGroupSelectedVariationRef because the vg products need to persist with it being manipulated.
  const virtualGroupingOptions = useMemo(
    () =>
      virtualGroupOptionsMap.map((product) => {
        return {
          name: product.name,
          variations: virtualGroupingVariationsRef.current,
          validOptions: product.validOptions,
        };
      }),
    [virtualGroupOptionsMap, virtualGroupingVariationsRef]
  );

  return (
    <div data-testid="virtualGroupingTileContainer" className={cls("my-5")}>
      {virtualGroupingOptions.map((product) => (
        <fieldset
          className={cls("pt-0", "pb-2.5", "px-0", "border-0", "mx-0")}
          key={product.name}
          aria-labelledby="currentTileSelection"
        >
          <div>
            <p
              data-testid="variationTitle"
              className={cls(
                "text-sm",
                "text-gray-800",
                "px-0",
                "mb-3",
                addToCartError?.sourceName === "vgSelection"
                  ? "text-red-700"
                  : ""
              )}
            >
              <span
                id="currentTileSelection"
                className={cls("font-semibold")}
              >{`${product.name}:`}</span>{" "}
              {getVariationTextOption(product.name)}
            </p>
          </div>
          <div
            ref={virtualGroupingTileContainerRef}
            data-testid="variationContainer"
            className={cls(
              "grid",
              "grid-cols-4",
              "gap-2",
              "overflow-x-hidden",
              "overflow-y-auto",
              product.variations.length > 8 ? "max-h-56" : "",
              "lt:pr-0.5"
            )}
          >
            <GroupConfiguarationTiles
              virtualGroupTileVariations={showEightVirtualGroupTileVariations(
                product.variations
              )}
              getValidOptions={product.validOptions}
              selectedOptions={selectedOptions}
              onButtonHoverEnter={onButtonHoverEnter}
              onButtonHoverLeave={onButtonHoverLeave}
              onlyFocusOnKeyboardEvent={onlyFocusOnKeyboardEvent}
              onClickPosition={onClickPosition}
              handleClickVirtualGroupingVariation={
                handleClickVirtualGroupingVariation
              }
            />
          </div>
          {product.variations.length > 8 && width <= 600 && (
            <Button
              id="viewAllVirtualGroupingItems"
              data-testid="showMoreVariations"
              type="button"
              className={cls(
                "mb-0",
                "bg-transparent",
                "w-full",
                "border",
                "border-solid",
                "border-gray-600",
                "rounded",
                "flex",
                "justify-center",
                "items-center",
                "text-center",
                "py-2",
                "text-green-700",
                "font-bold",
                "text-base",
                "leading-5",
                "mt-5"
              )}
              color="custom"
              size="custom"
              onClick={() => {
                setSelectedMasterTile(product);
                toggleShowMobileDrawerDialog(virtualGroupingMobileDialogRef);
              }}
            >
              {`View all ${
                product.variations.length
              } ${product.name.toLocaleLowerCase()}s`}
            </Button>
          )}
        </fieldset>
      ))}
      {width <= 600 ? (
        <ProductDetailVirtualGroupingMobileDrawer
          virtualGroupingMobileDialogRef={virtualGroupingMobileDialogRef}
          toggleShowMobileVirtualGroupingDrawer={() =>
            toggleShowMobileDrawerDialog(virtualGroupingMobileDialogRef)
          }
          selectedOptions={selectedOptions}
          virtualGroupingTileProduct={selectedMasterTile}
          handleClickVirtualGroupingVariation={
            handleClickVirtualGroupingVariation
          }
        />
      ) : null}
    </div>
  );
};

export default ProductDetailVirtualGroupingTile;
